import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { sortByLabel, sortByName } from "@/lib/sortRows";
import { isoDate, todayISO, toLocalISODate } from "@/lib/localDate";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlusIcon, CalendarIcon, CheckIcon, XIcon, CaretLeftIcon, CaretRightIcon, WarningIcon, PencilSimpleIcon, ArrowRightIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";
import PagedList from "@/components/PagedList";
import TableSearch, { matchesQuery } from "@/components/TableSearch";
import { roleLabel } from "@/lib/roleLabels";
import HolidaysPanel, { fmtHolidayDate } from "@/components/HolidaysPanel";

// Ordered alphabetically by LABEL, not by key — the keys ("wfh", "comp_off")
// don't sort the same way the user-visible text does. This is the order the type
// dropdown and the filter render in.
//
// "public_holiday" is deliberately NOT offered any more: company holidays come
// from the public_holidays list automatically (see HolidaysPanel.jsx), so
// nobody applies for them. TYPE_META keeps its label for older rows.
const TYPES = ["comp_off", "other", "personal", "sick", "vacation", "wfh"];
const STATUSES = ["planned", "approved", "rejected", "taken", "cancelled"];

const TYPE_META = {
  comp_off:       { label: "Comp Off",       cls: "text-teal-800 border-teal-200 bg-teal-50" },
  other:          { label: "Other",          cls: "text-zinc-700 border-zinc-300 bg-zinc-50" },
  personal:       { label: "Personal",       cls: "text-purple-800 border-purple-200 bg-purple-50" },
  public_holiday: { label: "Public Holiday", cls: "text-amber-800 border-amber-200 bg-amber-50" },
  sick:           { label: "Sick",           cls: "text-red-700 border-red-200 bg-red-50" },
  vacation:       { label: "Vacation",       cls: "text-blue-800 border-blue-200 bg-blue-50" },
  wfh:            { label: "WFH",            cls: "text-emerald-800 border-emerald-200 bg-emerald-50" },
};

const STATUS_META = {
  planned:   { label: "Planned",   dot: "#71717A" },
  approved:  { label: "Approved",  dot: "#10B981" },
  rejected:  { label: "Rejected",  dot: "#E3342F" },
  taken:     { label: "Taken",     dot: "#0176D3" },
  cancelled: { label: "Cancelled", dot: "#A1A1AA" },
};

// Keeps a leave's date range valid while it's being typed.
//
// Picking an END before the START pulls the start back to match it, which is
// what was asked for. The mirror case moves the END instead: whichever field
// you just edited wins, so the app never silently undoes the change you made.
//
// These are ISO yyyy-mm-dd strings, so a plain string comparison IS a date
// comparison — no parsing needed.
const withValidRange = (patch) => (prev) => {
  const next = { ...prev, ...patch };
  if (next.start_date && next.end_date && next.end_date < next.start_date) {
    if ("end_date" in patch) next.start_date = next.end_date;
    else next.end_date = next.start_date;
  }
  return next;
};

// Severity of an overlap, styled like StatusBadge so a risk pill and a status
// pill read as the same kind of object rather than two unrelated chips.
const SEVERITY_META = {
  high:   { label: "High risk",   dot: "#DC2626", bg: "bg-red-50",   text: "text-red-700",   border: "border-red-200" },
  medium: { label: "Medium risk", dot: "#D97706", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  low:    { label: "Low risk",    dot: "#64748B", bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
};

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/*
  Which week of its month an overlap starts in — the 1st to the 7th is week 1,
  the 8th to the 14th is week 2, and so on.

  Deliberately NOT ISO weeks. A manager scanning for clashes thinks in "the
  first week of August"; an ISO week number ("W35") means nothing without a
  lookup, and ISO weeks straddle month boundaries, which would put an August
  conflict under a July heading.

  The date string is split rather than passed to `new Date` — see
  lib/localDate.js for why a YYYY-MM-DD must never go near a Date here.
*/
function weekBucket(isoDay) {
  const [y, m, d] = String(isoDay).split("-").map(Number);
  const week = Math.floor((d - 1) / 7) + 1;
  const lastDay = new Date(y, m, 0).getDate(); // day 0 of the next month is the last of this one
  const from = (week - 1) * 7 + 1;
  const to = Math.min(week * 7, lastDay);
  return {
    key: `${y}-${String(m).padStart(2, "0")}-w${week}`,
    label: `${MONTH_SHORT[m - 1]} Week ${week}`,
    range: `${from}–${to} ${MONTH_SHORT[m - 1]} ${y}`,
  };
}

/*
  Day arithmetic on a YYYY-MM-DD string.

  The string is split into parts and handed to `new Date(y, m - 1, d + n)` —
  building a local Date from NUMBERS, which is safe, and reading it back through
  toLocalISODate, which never touches UTC. What must never happen is
  `new Date("2026-08-28")`: that parses as UTC midnight and reads back as the
  27th anywhere east of Greenwich. See lib/localDate.js.
*/
const addDays = (iso, n) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return toLocalISODate(new Date(y, m - 1, d + n));
};

/** The Monday of the week containing `iso`. Business weeks run Mon–Sun here. */
const mondayOf = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay(); // 0 = Sunday
  return addDays(iso, dow === 0 ? -6 : 1 - dow);
};

const DOW_INITIALS = ["M", "T", "W", "T", "F", "S", "S"];

// How far ahead the Conflicts tab looks: the current week plus the next four.
// A manager is deciding about the near term; anything past that is planning
// noise, and is collected under "Later" instead of crowding the strip.
const WEEKS_AHEAD = 4;

// Short form for the card footer — the year already sits in the week heading
// above it, so repeating it on every card is noise.
const fmtShort = (d) => {
  if (!d) return "—";
  const [, m, day] = String(d).split("-").map(Number);
  return `${day} ${MONTH_SHORT[m - 1]}`;
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function LeavesPage() {
  const { user: currentUser } = useAuth();
  const { search } = useLocation();
  // A leave-request/decision email's "Open leave request" button lands here —
  // see supabase/functions/notify-leave/index.ts.
  const highlightId = useMemo(() => new URLSearchParams(search).get("highlight"), [search]);
  const [tab, setTab] = useState("team");
  const [leaves, setLeaves] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  // The people in the caller's initiatives — the Leave Plan Master's member list.
  // `users` stays the full roster: the leave form's approver picker needs it.
  const [teamUsers, setTeamUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userLeaves, setUserLeaves] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [teamQuery, setTeamQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  // Guards createLeave against double-clicks / double-Enter — without this,
  // clicking Save twice (or a slow round-trip plus an impatient second
  // click) fired two identical POSTs and created a duplicate leave row; see
  // ProjectDetailPage.jsx's identical taskSubmitting guard, which this
  // mirrors. Confirmed live 2026-09-16: two rows for the same person/type/
  // dates, timestamped 0.47s apart.
  const [creatingLeave, setCreatingLeave] = useState(false);
  const today = todayISO();
  const [form, setForm] = useState({ type: "vacation", start_date: today, end_date: today, reason: "", status: "planned", manager_id: "" });
  // A person's leave list (used for both "My Leaves" and "By Member") opens
  // showing only current/upcoming leave — a leave that already fully ended
  // is history, hidden until asked for, rather than piling up under leaves
  // someone still needs to act on. Team Roster gets its own separate toggle
  // below, since it's a different list.
  const [showMyHistory, setShowMyHistory] = useState(false);
  const [showTeamHistory, setShowTeamHistory] = useState(false);

  // Editing an existing leave — only ever offered for the caller's OWN
  // leave (checked again server-side by RLS regardless of what the UI
  // shows) and only while it's still "planned". Once a manager/admin has
  // approved or rejected it, the trigger-enforced workflow means the
  // decision is final — editing type/dates/reason at that point would be
  // silently meaningless (the approval was for a specific request) or
  // actively misleading, so the edit control simply isn't offered.
  const [editingLeave, setEditingLeave] = useState(null); // the leave row being edited, or null
  const [editForm, setEditForm] = useState({ type: "vacation", start_date: today, end_date: today, reason: "" });
  const now = new Date();
  const [cal, setCal] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [conflicts, setConflicts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  // date -> holiday name, for the calendar and the "includes a holiday" hint.
  const holidayByDate = useMemo(() => new Map(holidays.map((h) => [h.holiday_date, h.name])), [holidays]);
  const holidaysIn = (start, end) => holidays.filter((h) => h.holiday_date >= start && h.holiday_date <= end);
  // Calendar days in [start, end], inclusive — split, never `new Date(iso)`.
  const calendarDays = (start, end) => {
    const [y1, m1, d1] = start.split("-").map(Number);
    const [y2, m2, d2] = end.split("-").map(Number);
    return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000) + 1;
  };
  // A range made up ONLY of holidays needs no leave at all.
  const allHolidays = (start, end) => !!start && !!end && holidaysIn(start, end).length >= calendarDays(start, end);
  const holidayHint = (start, end) => {
    if (!start || !end || end < start) return null;
    const hit = holidaysIn(start, end);
    if (hit.length === 0) return null;
    const days = calendarDays(start, end) - hit.length;
    return (
      <div className="text-xs rounded-sm p-2 border border-amber-200 bg-amber-50 text-amber-900" data-testid="leave-holiday-hint">
        {days === 0
          ? <>{hit.map((h) => h.name).join(", ")} {hit.length === 1 ? "is" : "are"} already a public holiday — no need to apply.</>
          : <>Includes {hit.map((h) => `${h.name} (${fmtHolidayDate(h.holiday_date)})`).join(", ")} — not counted. This leave is <strong>{days} day{days === 1 ? "" : "s"}</strong>.</>}
      </div>
    );
  };

  // The rolling horizon: this week, next week, and the three after that.
  //
  // A conflict lands in EVERY week its overlap touches, not just the week it
  // starts in — a clash running Thu-to-Tue is a problem in both weeks, and a
  // manager looking at next week needs to see it there too.
  //
  // `perDay` is what paints a day amber: the conflicts covering that exact
  // date. ISO strings compare as dates, so no parsing is needed anywhere here.
  const horizonWeeks = useMemo(() => {
    const firstMonday = mondayOf(today);
    return Array.from({ length: WEEKS_AHEAD + 1 }, (_, i) => {
      const start = addDays(firstMonday, i * 7);
      const end = addDays(start, 6);
      const days = Array.from({ length: 7 }, (_, d) => addDays(start, d));
      const items = conflicts.filter((c) => c.overlap_start <= end && c.overlap_end >= start);
      const perDay = {};
      days.forEach((day) => {
        perDay[day] = items.filter((c) => c.overlap_start <= day && c.overlap_end >= day);
      });
      return {
        key: start,
        start,
        end,
        days,
        items,
        perDay,
        label: i === 0 ? "This week" : i === 1 ? "Next week" : `Week of ${fmtShort(start)}`,
      };
    });
  }, [conflicts, today]);

  const horizonEnd = horizonWeeks[horizonWeeks.length - 1]?.end;

  // Anything starting past the horizon, kept in the original "Aug Week 2" style
  // grouping so a far-future clash is still visible rather than silently
  // dropped. Deliberately NOT ISO week numbers — a manager thinks in "the first
  // week of August", and ISO weeks straddle month boundaries.
  const laterWeeks = useMemo(() => {
    const byWeek = new Map();
    for (const c of conflicts) {
      if (horizonEnd && c.overlap_start <= horizonEnd) continue;
      const bucket = weekBucket(c.overlap_start);
      if (!byWeek.has(bucket.key)) byWeek.set(bucket.key, { ...bucket, items: [] });
      byWeek.get(bucket.key).items.push(c);
    }
    return [...byWeek.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [conflicts, horizonEnd]);

  const laterCount = laterWeeks.reduce((n, w) => n + w.items.length, 0);

  // "owner" has identical permissions to "manager" everywhere — see
  // is_manager_or_admin() in schema_owner_role.sql.
  const canAdmin = currentUser && ["admin", "manager", "owner"].includes(currentUser.role);
  // The mandatory "select a manager" dropdown on the leave form — same
  // pattern as ProjectsPage.jsx's project-manager picker. Admins are included
  // too: they can approve any leave, and a manager may hold the admin role.
  const allApprovers = users.filter((u) => ["manager", "owner", "admin"].includes(u.role) && !u.disabled);
  // Who may be chosen as approver: the managers/owners/admins among the people in my
  // initiatives, plus the person I report to and whoever they report to (up to three
  // levels — e.g. Nitish -> Amaaun -> Sandeep). The approver can read the leave they are
  // sent whoever they are (leaves RLS: manager_id = auth.uid()). If this would leave
  // the list empty, fall back to the full list rather than blocking the form.
  const approverIds = new Set(teamUsers.map((u) => u._id));
  let bossId = users.find((u) => u._id === currentUser?.id)?.reports_to;
  for (let i = 0; i < 3 && bossId; i++) {
    approverIds.add(bossId);
    bossId = users.find((u) => u._id === bossId)?.reports_to;
  }
  const scopedApprovers = allApprovers.filter((u) => approverIds.has(u._id));
  const managers = scopedApprovers.length > 0 ? scopedApprovers : allApprovers;

  const loadTeam = async () => {
    const [l, s, u, c, t] = await Promise.all([
      api.get("/leaves"),
      api.get("/leaves/summary"),
      api.get("/users"),
      api.get("/leaves/conflicts").catch(() => ({ data: { conflicts: [] } })),
      api.get("/team/members").catch(() => null),
    ]);
    setLeaves(l.data);
    setSummary(s.data);
    setUsers(sortByName(u.data));
    // If the scoped list can't be fetched, fall back to the full roster rather than an empty page.
    setTeamUsers(sortByName((t ? t.data : u.data) || []));
    setConflicts(c.data?.conflicts || []);
    // currentUser comes from AuthContext (a raw Supabase auth user merged
    // with its profile row) — it only ever has `.id`, never `._id` (that
    // mapping only happens for table rows fetched through lib/api.js). This
    // was silently defaulting selectedUserId to undefined on every load.
    if (!selectedUserId && currentUser) setSelectedUserId(currentUser.id);
  };

  // Separate from loadTeam so a missing/failed holiday list never blocks the
  // rest of the page.
  const loadHolidays = async () => {
    try {
      const { data } = await api.get("/holidays");
      setHolidays(data || []);
    } catch { setHolidays([]); }
  };
  // After an admin edits the list, day counts everywhere change too.
  const onHolidaysChanged = async () => { await Promise.all([loadHolidays(), loadTeam(), loadUser()]); };

  const loadUser = async () => {
    if (!selectedUserId) return;
    const { data } = await api.get(`/users/${selectedUserId}/leaves`);
    setUserLeaves(data);
  };

  useEffect(() => { loadTeam(); loadHolidays(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadUser(); /* eslint-disable-next-line */ }, [selectedUserId]);

  // A leave-request/decision email's deep link (?highlight=<leave id>) —
  // reveal the leave rather than leaving it hidden behind whatever filter
  // happened to be set, INCLUDING the current/upcoming-only default: a
  // decision email linking back to a leave that's since moved into the past
  // must still land on it, not on an empty "no current leaves" screen.
  // Runs once the team-wide leaves list has loaded.
  useEffect(() => {
    if (!highlightId || leaves.length === 0) return;
    const hit = leaves.find((l) => l._id === highlightId);
    if (!hit) return;
    setShowMyHistory(true);
    if (canAdmin) {
      setTab("team");
      setStatusFilter("all");
      setTeamQuery("");
      setShowTeamHistory(true);
      setSelectedUserId(hit.user_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, leaves, canAdmin]);

  // Does `userId` already have a leave overlapping [start, end]? Rejected/
  // cancelled leaves never happened, so they don't block a new one over the
  // same dates. `excludeId` skips the row being edited, against itself.
  //
  // Reads from `leaves` (the team-wide list, loaded for everyone regardless
  // of role — see loadTeam above) rather than `userLeaves`: `userLeaves` only
  // tracks whichever member is currently selected in the "By Member" tab,
  // which is not necessarily the person being saved (an admin editing their
  // OWN leave inline from Team Roster while a different member is selected
  // there, for instance).
  const overlappingLeave = (userId, start, end, excludeId = null) =>
    leaves.find((l) =>
      l.user_id === userId &&
      l._id !== excludeId &&
      l.status !== "rejected" && l.status !== "cancelled" &&
      start <= l.end_date && l.start_date <= end
    );

  const createLeave = async () => {
    if (creatingLeave) return; // already in flight — a second click while the first is still saving must be a no-op, not a second insert
    if (!selectedUserId) { toast.error("Select a member"); return; }
    if (!form.manager_id) { toast.error("Select a manager"); return; }
    if (form.end_date < form.start_date) { toast.error("End date must be on/after start date"); return; }
    if (allHolidays(form.start_date, form.end_date)) { toast.error("Those dates are already public holidays — no need to apply"); return; }
    const clash = overlappingLeave(selectedUserId, form.start_date, form.end_date);
    if (clash) {
      toast.error(`Overlaps an existing ${TYPE_META[clash.type]?.label || clash.type} leave (${fmt(clash.start_date)} → ${fmt(clash.end_date)})`);
      return;
    }
    setCreatingLeave(true);
    try {
      await api.post(`/users/${selectedUserId}/leaves`, form);
      toast.success("Leave planned");
      setForm({ type: "vacation", start_date: today, end_date: today, reason: "", status: "planned", manager_id: "" });
      setAddOpen(false);
      await Promise.all([loadTeam(), loadUser()]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setCreatingLeave(false); }
  };

  const setStatus = async (lid, status) => {
    try {
      await api.patch(`/leaves/${lid}`, { status });
      toast.success(STATUS_META[status].label);
      await Promise.all([loadTeam(), loadUser()]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  // canEditLeave: own leave, still pending. Checked here purely for what
  // the UI offers — RLS (owner-or-manager row check) plus the
  // enforce_leave_status_rules trigger (which only ever gates a STATUS
  // change, not these fields) are what actually enforce "own leave only"
  // and don't otherwise care about current status, so this button being
  // hidden is a UX choice on top of an already-safe backend, not the only
  // thing standing between a user and someone else's leave.
  const canEditLeave = (l) => l.user_id === currentUser?.id && l.status === "planned";

  const openEdit = (l) => {
    setEditingLeave(l);
    setEditForm({ type: l.type, start_date: l.start_date, end_date: l.end_date, reason: l.reason || "" });
  };

  const saveEdit = async () => {
    if (editForm.end_date < editForm.start_date) { toast.error("End date must be on/after start date"); return; }
    if (allHolidays(editForm.start_date, editForm.end_date)) { toast.error("Those dates are already public holidays — no need to apply"); return; }
    const clash = overlappingLeave(editingLeave.user_id, editForm.start_date, editForm.end_date, editingLeave._id);
    if (clash) {
      toast.error(`Overlaps an existing ${TYPE_META[clash.type]?.label || clash.type} leave (${fmt(clash.start_date)} → ${fmt(clash.end_date)})`);
      return;
    }
    try {
      await api.patch(`/leaves/${editingLeave._id}`, editForm);
      toast.success("Leave updated");
      setEditingLeave(null);
      await Promise.all([loadTeam(), loadUser()]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };


  // A leave that already fully ended is history; still-running or
  // not-yet-started counts as current/future. Rejected/cancelled leaves
  // never happened, but they're dated like any other row and fall out of
  // view the same way once their (non-)dates pass — nobody needs a
  // rejected request from three months ago cluttering the default view
  // either.
  const isCurrentOrFuture = (l) => l.end_date >= today;

  // Type and status are searchable by their LABEL, not their key: nobody looks
  // for "wfh" or "public_holiday" — they type "Work From Home" or "Holiday",
  // which is what the row actually shows.
  const filteredTeam = useMemo(
    () =>
      leaves
        .filter((l) => showTeamHistory || isCurrentOrFuture(l))
        .filter((l) => statusFilter === "all" || l.status === statusFilter)
        .filter((l) =>
          matchesQuery(
            teamQuery,
            l.user_name, l.reason,
            TYPE_META[l.type]?.label, STATUS_META[l.status]?.label
          )
        ),
    [leaves, statusFilter, teamQuery, showTeamHistory, today]
  );
  // Shared by "My Leaves" (member self-view) and "By Member" (admin
  // per-person view) — both render the same userLeaves array the same way,
  // so one history toggle/filter serves both instead of two copies.
  const visibleUserLeaves = useMemo(
    () => userLeaves.filter((l) => showMyHistory || isCurrentOrFuture(l)),
    [userLeaves, showMyHistory, today]
  );
  const myHistoryCount = userLeaves.length - visibleUserLeaves.length;
  const selectedUser = users.find((u) => u._id === selectedUserId);
  const myStats = summary?.per_user?.find((p) => p.user_id === currentUser?.id) || { total_days: 0, planned: 0, approved: 0, taken: 0 };

  // Shared across both the member-only view and the manager/admin console
  // below (two separate `return`s) so there's one edit dialog, not two
  // copies to keep in sync.
  const editDialog = (
    <Dialog open={!!editingLeave} onOpenChange={(o) => { if (!o) setEditingLeave(null); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="edit-leave-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit leave</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="trk-uppercase-label mb-2 block">Type</Label>
            <Select value={editForm.type} onValueChange={(v) => setEditForm({ ...editForm, type: v })}>
              <SelectTrigger className="rounded-sm" data-testid="edit-leave-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="trk-uppercase-label mb-2 block">Start</Label>
              <Input type="date" value={editForm.start_date} onChange={(e) => setEditForm(withValidRange({ start_date: e.target.value }))} className="rounded-sm" data-testid="edit-leave-start-input" />
            </div>
            <div>
              <Label className="trk-uppercase-label mb-2 block">End</Label>
              <Input type="date" value={editForm.end_date} onChange={(e) => setEditForm(withValidRange({ end_date: e.target.value }))} className="rounded-sm" data-testid="edit-leave-end-input" />
            </div>
          </div>
          {holidayHint(editForm.start_date, editForm.end_date)}
          <div>
            <Label className="trk-uppercase-label mb-2 block">Reason (optional)</Label>
            <Textarea value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} rows={2} className="rounded-sm" data-testid="edit-leave-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditingLeave(null)} className="rounded-sm">Cancel</Button>
          <Button onClick={saveEdit} data-testid="save-edit-leave-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // A plain member gets a dedicated, self-only view — not a filtered copy
  // of the manager console. "Team Roster"/"By Member"/"Conflicts" all imply
  // team-wide visibility even though RLS already scopes what data actually
  // comes back; showing them to a member is confusing at best (a "Team
  // Roster" that only ever contains one row) and contradicts the intent
  // ("only see their own leave, not the whole one") at worst.
  if (!canAdmin) {
    return (
      <AppLayout>
        <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
          <div>
            <div className="trk-uppercase-label">People Ops</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">My Leaves</h1>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-leave-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                <PlusIcon size={14} weight="bold" className="mr-1" /> Apply for leave
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-md">
              <DialogHeader><DialogTitle className="font-display">Apply for leave</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="trk-uppercase-label mb-2 block">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="trk-uppercase-label mb-2 block">Manager <span className="text-red-600">*</span></Label>
                  {/* Mandatory: createLeave() refuses to submit without one.
                      Emails this manager on save — see notify-leave. */}
                  <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="leave-manager-select"><SelectValue placeholder="Select a manager" /></SelectTrigger>
                    {/* Excludes selectedUserId — the person this leave is
                        FOR (yourself here, or whoever's picked in "By
                        Member") — nobody can be their own manager on a
                        leave; see leaves_manager_not_self in
                        schema_leaves_decide.sql for the hard backstop. */}
                    <SelectContent>{managers.filter((m) => m._id !== selectedUserId).map((m) => <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="trk-uppercase-label mb-2 block">Start</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm(withValidRange({ start_date: e.target.value }))} className="rounded-sm" data-testid="leave-start-input" />
                  </div>
                  <div>
                    <Label className="trk-uppercase-label mb-2 block">End</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm(withValidRange({ end_date: e.target.value }))} className="rounded-sm" data-testid="leave-end-input" />
                  </div>
                </div>
                {holidayHint(form.start_date, form.end_date)}
                <div>
                  <Label className="trk-uppercase-label mb-2 block">Reason (optional)</Label>
                  <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} className="rounded-sm" data-testid="leave-reason-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createLeave} disabled={creatingLeave} data-testid="save-leave-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">{creatingLeave ? "Saving…" : "Save"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="px-8 py-6 trk-fade-in">
          <div className="grid grid-cols-4 border border-border rounded-sm overflow-hidden bg-white mb-6">
            {[
              { k: "total_days", label: "Total Days", v: myStats.total_days },
              { k: "planned", label: "Planned", v: myStats.planned },
              { k: "approved", label: "Approved", v: myStats.approved },
              { k: "taken", label: "Taken", v: myStats.taken },
            ].map((c, i) => (
              <div key={c.k} className={`p-4 ${i < 3 ? "border-r border-border" : ""}`}>
                <div className="trk-uppercase-label">{c.label}</div>
                <div className="font-display font-extrabold text-3xl mt-1 tabular-nums">{c.v}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg font-bold">{showMyHistory ? "All leave" : "Current & upcoming"}</h2>
            <button
              onClick={() => setShowMyHistory((v) => !v)}
              data-testid="toggle-my-history"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-border rounded-sm hover:bg-zinc-50 text-zinc-600"
            >
              <ClockCounterClockwiseIcon size={13} />
              {showMyHistory ? "Hide history" : myHistoryCount > 0 ? `History (${myHistoryCount})` : "History"}
            </button>
          </div>

          <div className="border border-border rounded-sm overflow-hidden bg-white">
            {visibleUserLeaves.length === 0 ? (
              <div className="p-10 text-center text-sm text-zinc-500">
                <CalendarIcon size={28} className="mx-auto text-zinc-300 mb-2" />
                {userLeaves.length === 0 ? "You haven't applied for any leave yet." : "No current or upcoming leave — click History above to see past leave."}
              </div>
            ) : (
              <PagedList items={visibleUserLeaves} pageSize={10} testIdPrefix="my-leaves" className="px-5 pb-3">
                {(rows) => (
                  <div className="-mx-5">
                {rows.map((l) => (
                  <div key={l._id} data-testid={`my-leave-${l._id}`} className={`px-5 py-3 border-b border-border last:border-0 flex items-center justify-between ${l._id === highlightId ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-zinc-50"}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border ${TYPE_META[l.type]?.cls || TYPE_META.other.cls}`}>
                        {TYPE_META[l.type]?.label}
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{fmt(l.start_date)} → {fmt(l.end_date)} <span className="text-zinc-500 font-mono">· {l.days}d</span></div>
                        {l.reason && <div className="text-xs text-zinc-500 break-words">{l.reason}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_META[l.status]?.dot }} />
                        {STATUS_META[l.status]?.label}
                      </span>
                      {canEditLeave(l) && (
                        <button
                          onClick={() => openEdit(l)}
                          data-testid={`edit-my-leave-${l._id}`}
                          className="p-1 border border-border hover:bg-zinc-50 rounded-sm text-zinc-600"
                          title="Edit"
                        >
                          <PencilSimpleIcon size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                  </div>
                )}
              </PagedList>
            )}
          </div>

          <div className="mt-6">
            <HolidaysPanel holidays={holidays} isAdmin={false} onChanged={onHolidaysChanged} />
          </div>
        </div>
        {editDialog}
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">People Ops</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Leave Plan Master</h1>
        </div>
        {summary && (
          <div className="flex items-center gap-6 text-xs font-mono">
            {Object.entries(summary.per_status).map(([s, n]) => (
              <div key={s} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_META[s]?.dot }} />
                <span className="text-zinc-500">{STATUS_META[s]?.label}</span>
                <span className="tabular-nums text-zinc-900">{n}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-8 py-6 trk-fade-in">
        <Tabs value={tab} onValueChange={setTab}>
          {/* Tabs and the status filter share one row — the filter used to sit
              on a line of its own below them, costing a full row of vertical
              space to hold a single 160px control. It only applies to the Team
              Roster grid, so it appears only on that tab rather than sitting
              inert next to Calendar and Conflicts. */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <TabsList className="rounded-sm">
              <TabsTrigger value="team" data-testid="leaves-tab-team">Team Roster</TabsTrigger>
              <TabsTrigger value="member" data-testid="leaves-tab-member">By Member</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="leaves-tab-calendar">Calendar</TabsTrigger>
              <TabsTrigger value="conflicts" data-testid="leaves-tab-conflicts">
                Conflicts{conflicts.length > 0 && <span className="ml-1 text-[9px] font-mono bg-amber-500 text-white px-1 rounded-sm">{conflicts.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="holidays" data-testid="leaves-tab-holidays">Holidays</TabsTrigger>
            </TabsList>

            {tab === "team" && (
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="rounded-sm w-40" data-testid="leave-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {sortByLabel(STATUSES, (s) => STATUS_META[s].label).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {/* Team Roster opens on current/upcoming leave only — a leave
                    that already ended is history, one click away, not mixed
                    in with what still needs attention. */}
                <button
                  onClick={() => setShowTeamHistory((v) => !v)}
                  data-testid="toggle-team-history"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-border rounded-sm hover:bg-zinc-50 text-zinc-600"
                >
                  <ClockCounterClockwiseIcon size={13} />
                  {showTeamHistory ? "Hide history" : "History"}
                </button>
              </div>
            )}
          </div>

          <TabsContent value="team">
            <DataTable
              toolbarStart={
                <TableSearch
                  value={teamQuery}
                  onChange={setTeamQuery}
                  placeholder="Search member, type or reason…"
                  label="Search team leaves"
                  testid="leaves-search"
                  widthCls="w-64"
                />
              }
              storageKey="leaves.visibleColumns"
              testIdPrefix="leave"
              rows={filteredTeam}
              rowKey={(l) => l._id}
              rowClassName={(l) => (l._id === highlightId ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-zinc-50")}
              pageSize={10}
              emptyMessage={showTeamHistory ? "No leaves match this filter." : "No current or upcoming leaves match this filter — click History above to see past leave."}
              columns={[
                {
                  key: "member", label: "Member", locked: true, cls: "px-4",
                  cell: (l) => (
                    <div className="min-w-0">
                      <Link to={`/team/${l.user_id}`} className="block break-words font-medium hover:text-[#0176D3]" title={l.user_name}>{l.user_name}</Link>
                      <div className="break-words text-xs text-zinc-500">{roleLabel(l.user_role)}</div>
                    </div>
                  ),
                },
                {
                  key: "type", label: "Type", cls: "px-3", weight: 116,
                  cell: (l) => (
                    <span className={`inline-flex max-w-full items-center break-words rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${TYPE_META[l.type]?.cls || TYPE_META.other.cls}`}>
                      {TYPE_META[l.type]?.label || l.type}
                    </span>
                  ),
                },
                {
                  key: "dates", label: "Dates", cls: "px-3", weight: 192,
                  cell: (l) => <span className="font-mono text-xs text-zinc-700">{fmt(l.start_date)} → {fmt(l.end_date)}</span>,
                },
                {
                  key: "days", label: "Days", cls: "px-3 text-right", weight: 80,
                  cell: (l) => <span className="font-mono tabular-nums">{l.days}</span>,
                },
                {
                  key: "reason", label: "Reason", cls: "px-4",
                  cell: (l) => <div className="break-words text-zinc-600" title={l.reason || ""}>{l.reason || "—"}</div>,
                },
                {
                  key: "status", label: "Status", cls: "px-3", weight: 116,
                  cell: (l) => (
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: STATUS_META[l.status]?.dot }} />
                      {STATUS_META[l.status]?.label}
                    </span>
                  ),
                },
                {
                  key: "actions", label: "Actions", head: "", hideFromPicker: true, cls: "px-3 text-right", weight: 96,
                  cell: (l) => (
                    <div className="inline-flex gap-1">
                      {canAdmin && l.status === "planned" && (
                        <>
                          <button
                            onClick={() => setStatus(l._id, "approved")}
                            data-testid={`approve-${l._id}`}
                            className="p-1 border border-emerald-200 hover:bg-emerald-50 rounded-sm text-emerald-700"
                            title="Approve"
                          >
                            <CheckIcon size={12} weight="bold" />
                          </button>
                          <button
                            onClick={() => setStatus(l._id, "rejected")}
                            data-testid={`reject-${l._id}`}
                            className="p-1 border border-red-200 hover:bg-red-50 rounded-sm text-red-700"
                            title="Reject"
                          >
                            <XIcon size={12} weight="bold" />
                          </button>
                        </>
                      )}
                      {canEditLeave(l) && (
                        <button
                          onClick={() => openEdit(l)}
                          data-testid={`edit-leave-${l._id}`}
                          className="p-1 border border-border hover:bg-zinc-50 rounded-sm text-zinc-600"
                          title="Edit (your own, still pending)"
                        >
                          <PencilSimpleIcon size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </TabsContent>

          <TabsContent value="member">
            <div className="grid lg:grid-cols-[280px_1fr] gap-6">
              <div className="border border-border rounded-sm">
                <div className="p-3 border-b border-border trk-uppercase-label">Members ({teamUsers.length})</div>
                <div className="max-h-[70vh] overflow-y-auto">
                  {teamUsers.map((u) => {
                    const stats = summary?.per_user?.find((p) => p.user_id === u._id);
                    return (
                      <button
                        key={u._id}
                        onClick={() => setSelectedUserId(u._id)}
                        data-testid={`leaves-member-${u._id}`}
                        className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-zinc-50 transition-colors ${
                          selectedUserId === u._id ? "bg-[#EFF6FF] border-l-2 border-l-[#0176D3]" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-sm break-words">{u.name}</div>
                            <div className="text-xs text-zinc-500 break-words">{roleLabel(u.role).toUpperCase()}</div>
                          </div>
                          <span className="font-mono text-xs text-zinc-400">{stats?.total_days || 0}d</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="border border-border rounded-sm">
                {selectedUser ? (
                  <>
                    <div className="p-5 border-b border-border flex items-center justify-between">
                      <div>
                        <div className="trk-uppercase-label">{roleLabel(selectedUser.role)}</div>
                        <h2 className="font-display text-2xl font-extrabold tracking-tight">{selectedUser.name}</h2>
                        <div className="text-xs text-zinc-500">{selectedUser.email}</div>
                      </div>
                      <Dialog open={addOpen} onOpenChange={setAddOpen}>
                        <DialogTrigger asChild>
                          <Button data-testid="add-leave-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                            <PlusIcon size={14} weight="bold" className="mr-1" /> Plan Leave
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm max-w-md">
                          <DialogHeader><DialogTitle className="font-display">Plan a leave</DialogTitle></DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label className="trk-uppercase-label mb-2 block">Type</Label>
                              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                <SelectTrigger className="rounded-sm" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="trk-uppercase-label mb-2 block">Manager <span className="text-red-600">*</span></Label>
                              {/* Mandatory here too: planning leave on someone's
                                  behalf still needs somebody to notify. */}
                              <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                                <SelectTrigger className="rounded-sm" data-testid="leave-manager-select"><SelectValue placeholder="Select a manager" /></SelectTrigger>
                                <SelectContent>{managers.map((m) => <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="trk-uppercase-label mb-2 block">Start</Label>
                                <Input type="date" value={form.start_date} onChange={(e) => setForm(withValidRange({ start_date: e.target.value }))} className="rounded-sm" data-testid="leave-start-input" />
                              </div>
                              <div>
                                <Label className="trk-uppercase-label mb-2 block">End</Label>
                                <Input type="date" value={form.end_date} onChange={(e) => setForm(withValidRange({ end_date: e.target.value }))} className="rounded-sm" data-testid="leave-end-input" />
                              </div>
                            </div>
                            {holidayHint(form.start_date, form.end_date)}
                            <div>
                              <Label className="trk-uppercase-label mb-2 block">Reason (optional)</Label>
                              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} className="rounded-sm" data-testid="leave-reason-input" />
                            </div>
                            {canAdmin && (
                              <div>
                                <Label className="trk-uppercase-label mb-2 block">Initial Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                  <SelectTrigger className="rounded-sm" data-testid="leave-initial-status"><SelectValue /></SelectTrigger>
                                  <SelectContent>{sortByLabel(STATUSES, (s) => STATUS_META[s].label).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          <DialogFooter>
                            <Button onClick={createLeave} disabled={creatingLeave} data-testid="save-leave-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">{creatingLeave ? "Saving…" : "Save"}</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Summary strip */}
                    {(() => {
                      const s = summary?.per_user?.find((p) => p.user_id === selectedUserId) || { total_days: 0, planned: 0, approved: 0, taken: 0 };
                      return (
                        <div className="grid grid-cols-4 border-b border-border">
                          {[
                            { k: "total_days", label: "Total Days", v: s.total_days },
                            { k: "planned", label: "Planned", v: s.planned },
                            { k: "approved", label: "Approved", v: s.approved },
                            { k: "taken", label: "Taken", v: s.taken },
                          ].map((c, i) => (
                            <div key={c.k} className={`p-4 ${i < 3 ? "border-r border-border" : ""}`}>
                              <div className="trk-uppercase-label">{c.label}</div>
                              <div className="font-display font-extrabold text-3xl mt-1 tabular-nums">{c.v}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    <div className="flex items-center justify-between px-5 pt-4 pb-1">
                      <span className="trk-uppercase-label">{showMyHistory ? "All leave" : "Current & upcoming"}</span>
                      <button
                        onClick={() => setShowMyHistory((v) => !v)}
                        data-testid="toggle-member-history"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-border rounded-sm hover:bg-zinc-50 text-zinc-600"
                      >
                        <ClockCounterClockwiseIcon size={13} />
                        {showMyHistory ? "Hide history" : myHistoryCount > 0 ? `History (${myHistoryCount})` : "History"}
                      </button>
                    </div>

                    {visibleUserLeaves.length === 0 ? (
                      <div className="p-10 text-center text-sm text-zinc-500">
                        {userLeaves.length === 0 ? "No leaves for this member yet." : "No current or upcoming leave — click History above to see past leave."}
                      </div>
                    ) : (
                      <PagedList items={visibleUserLeaves} pageSize={10} testIdPrefix="member-leaves" className="px-5 pb-3">
                        {(rows) => (
                          <div className="-mx-5">
                        {rows.map((l) => (
                          <div key={l._id} data-testid={`user-leave-${l._id}`} className={`px-5 py-3 border-b border-border last:border-0 flex items-center justify-between ${l._id === highlightId ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-zinc-50"}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border ${TYPE_META[l.type]?.cls || TYPE_META.other.cls}`}>
                                {TYPE_META[l.type]?.label}
                              </span>
                              <div className="min-w-0">
                                <div className="font-medium text-sm">{fmt(l.start_date)} → {fmt(l.end_date)} <span className="text-zinc-500 font-mono">· {l.days}d</span></div>
                                {l.reason && <div className="text-xs text-zinc-500 break-words">{l.reason}</div>}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center gap-1.5 text-xs">
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_META[l.status]?.dot }} />
                                {STATUS_META[l.status]?.label}
                              </span>
                              {canAdmin && l.status === "planned" && (
                                <div className="flex gap-1">
                                  <button onClick={() => setStatus(l._id, "approved")} className="p-1 border border-emerald-200 hover:bg-emerald-50 rounded-sm text-emerald-700"><CheckIcon size={12} weight="bold" /></button>
                                  <button onClick={() => setStatus(l._id, "rejected")} className="p-1 border border-red-200 hover:bg-red-50 rounded-sm text-red-700"><XIcon size={12} weight="bold" /></button>
                                </div>
                              )}
                              {canEditLeave(l) && (
                                <button onClick={() => openEdit(l)} title="Edit (your own, still pending)" className="p-1 border border-border hover:bg-zinc-50 rounded-sm text-zinc-600"><PencilSimpleIcon size={12} weight="bold" /></button>
                              )}
                            </div>
                          </div>
                        ))}
                          </div>
                        )}
                      </PagedList>
                    )}
                  </>
                ) : (
                  <div className="p-10 text-sm text-zinc-500">Select a member from the list.</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* --- CALENDAR --- */}
          <TabsContent value="calendar">
            {(() => {
              const monthName = new Date(cal.y, cal.m, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
              const first = new Date(cal.y, cal.m, 1);
              const daysInMonth = new Date(cal.y, cal.m + 1, 0).getDate();
              const startDay = first.getDay(); // 0=Sun
              const cells = [];
              for (let i = 0; i < startDay; i++) cells.push(null);
              for (let d = 1; d <= daysInMonth; d++) cells.push(d);
              while (cells.length % 7 !== 0) cells.push(null);
              // Built as strings, never via toISOString — see lib/localDate.js.
              const monthStart = isoDate(cal.y, cal.m, 1);
              const monthEnd = isoDate(cal.y, cal.m, daysInMonth);
              const inMonth = leaves.filter((l) => l.start_date <= monthEnd && l.end_date >= monthStart && l.status !== "cancelled" && l.status !== "rejected");

              const prev = () => setCal({ y: cal.m === 0 ? cal.y - 1 : cal.y, m: cal.m === 0 ? 11 : cal.m - 1 });
              const next = () => setCal({ y: cal.m === 11 ? cal.y + 1 : cal.y, m: cal.m === 11 ? 0 : cal.m + 1 });
              const goToday = () => setCal({ y: new Date().getFullYear(), m: new Date().getMonth() });

              const leavesForDay = (day) => {
                if (!day) return [];
                const iso = isoDate(cal.y, cal.m, day);
                return inMonth.filter((l) => l.start_date <= iso && l.end_date >= iso);
              };

              const totalDaysInMonth = inMonth.reduce((acc, l) => {
                const s = l.start_date > monthStart ? l.start_date : monthStart;
                const e = l.end_date < monthEnd ? l.end_date : monthEnd;
                // Company holidays inside the span aren't leave days.
                return acc + Math.max(0, calendarDays(s, e) - holidaysIn(s, e).length);
              }, 0);

              return (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <button onClick={prev} data-testid="cal-prev" className="p-2 border border-border rounded-sm hover:bg-zinc-50"><CaretLeftIcon size={14} weight="bold" /></button>
                      <button onClick={next} data-testid="cal-next" className="p-2 border border-border rounded-sm hover:bg-zinc-50"><CaretRightIcon size={14} weight="bold" /></button>
                      <button onClick={goToday} data-testid="cal-today" className="px-3 py-2 border border-border rounded-sm hover:bg-zinc-50 text-xs font-mono uppercase tracking-wider">Today</button>
                      <h2 className="font-display text-2xl font-extrabold tracking-tight ml-2" data-testid="cal-month-label">{monthName}</h2>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-mono">
                      <span className="text-zinc-500">{inMonth.length} leaves</span>
                      <span className="text-zinc-500">·</span>
                      <span className="text-zinc-900 tabular-nums">{totalDaysInMonth} person-days</span>
                    </div>
                  </div>

                  <div className="border border-border rounded-sm overflow-hidden">
                    <div className="grid grid-cols-7 bg-zinc-50 border-b border-border">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                        <div key={d} className="px-3 py-2 trk-uppercase-label border-r border-border last:border-0">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7">
                      {cells.map((day, i) => {
                        const dayLeaves = leavesForDay(day);
                        const isToday = day && new Date(cal.y, cal.m, day).toDateString() === new Date().toDateString();
                        // A company holiday: the whole cell is tinted and named,
                        // since it applies to everyone at once.
                        const holidayName = day ? holidayByDate.get(isoDate(cal.y, cal.m, day)) : null;
                        return (
                          <div key={i} className={`min-h-[92px] border-r border-b border-border last:border-r-0 p-1.5 ${!day ? "bg-zinc-50/50" : holidayName ? "bg-amber-50" : "bg-white"}`}>
                            {day && (
                              <>
                                <div className={`text-xs font-mono mb-1 flex items-center justify-between ${isToday ? "text-[#0176D3] font-bold" : "text-zinc-500"}`}>
                                  <span>{day}</span>
                                  {isToday && <span className="w-1.5 h-1.5 rounded-full bg-[#0176D3]" />}
                                </div>
                                {holidayName && (
                                  <div data-testid={`cal-day-${day}-holiday`} title={`Public holiday — ${holidayName}`}
                                       className="text-[10px] leading-tight px-1.5 py-0.5 mb-0.5 rounded-sm bg-amber-200/70 text-amber-900 font-medium break-words">
                                    {holidayName}
                                  </div>
                                )}
                                <div className="space-y-0.5">
                                  {dayLeaves.slice(0, 3).map((l) => (
                                    <div
                                      key={l._id}
                                      data-testid={`cal-day-${day}-leave-${l._id}`}
                                      title={`${l.user_name} — ${TYPE_META[l.type]?.label} (${STATUS_META[l.status]?.label})`}
                                      className={`text-[10px] leading-tight px-1.5 py-0.5 rounded-sm border break-words ${TYPE_META[l.type]?.cls || TYPE_META.other.cls} ${l.status === "planned" ? "border-dashed" : ""}`}
                                    >
                                      {l.user_name.split(" ")[0]}
                                    </div>
                                  ))}
                                  {dayLeaves.length > 3 && (
                                    <div className="text-[10px] font-mono text-zinc-500 px-1">+{dayLeaves.length - 3} more</div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-3 mt-4 text-xs">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-amber-200/70 text-amber-900 font-medium">Company holiday</span>
                    {TYPES.map((k) => (
                      <span key={k} className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border ${TYPE_META[k].cls}`}>{TYPE_META[k].label}</span>
                    ))}
                    <span className="text-zinc-500 ml-2 font-mono">Dashed border = planned (not yet approved)</span>
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          {/* --- CONFLICTS --- */}
          {/* Week-by-week, not a flat list: the question being asked is "is any
              week coming up too thin to cover?", and that is answered by a
              calendar strip with the clashing days lit up, not by paging
              through pairs. */}
          <TabsContent value="conflicts">
            <div className="space-y-5">
              {conflicts.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="conflicts-all-clear">
                  <CheckIcon size={16} weight="bold" />
                  No overlapping leaves for teammates on the same project.
                </div>
              ) : (
                <div className="text-sm text-zinc-600 flex items-center gap-2">
                  <WarningIcon size={16} weight="fill" className="text-amber-600" />
                  {conflicts.length} leave conflict{conflicts.length > 1 ? "s" : ""} across shared projects.
                  Amber marks a day two teammates on one project are both away.
                </div>
              )}

              <div className="space-y-4" data-testid="conflict-week-strip">
                {horizonWeeks.map((w) => (
                  <section
                    key={w.key}
                    data-testid={`conflict-week-${w.key}`}
                    className={`border rounded-sm overflow-hidden ${w.items.length > 0 ? "border-amber-300" : "border-border"}`}
                  >
                    <div className={`flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 border-b ${w.items.length > 0 ? "bg-amber-50 border-amber-200" : "bg-zinc-50/60 border-border"}`}>
                      <div className="flex items-baseline gap-2">
                        <h3 className="font-display text-base font-bold tracking-tight">{w.label}</h3>
                        <span className="trk-uppercase-label">{fmtShort(w.start)}{"\u2013"}{fmtShort(w.end)}</span>
                      </div>
                      {w.items.length > 0 ? (
                        <span className="text-[10px] font-mono uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded-sm">
                          {w.items.length} conflict{w.items.length > 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Clear</span>
                      )}
                    </div>

                    {/* The seven days. A day is amber when a conflict covers
                        that exact date, so the strip points at the day to fix
                        rather than just the week. */}
                    <div className="grid grid-cols-7 divide-x divide-border border-b border-border">
                      {w.days.map((day, di) => {
                        const hits = w.perDay[day] || [];
                        const isToday = day === today;
                        const isWeekend = di >= 5;
                        return (
                          <div
                            key={day}
                            title={hits.length > 0
                              ? hits.map((c) => `${c.project_title}: ${c.member_a.name} & ${c.member_b.name}`).join("\n")
                              : undefined}
                            data-testid={`conflict-day-${day}`}
                            data-conflicts={hits.length}
                            className={`px-2 py-2.5 text-center ${hits.length > 0 ? "bg-amber-100" : isWeekend ? "bg-zinc-50/60" : "bg-white"}`}
                          >
                            <div className={`text-[10px] font-mono uppercase tracking-wider ${hits.length > 0 ? "text-amber-700" : "text-zinc-400"}`}>
                              {DOW_INITIALS[di]}
                            </div>
                            <div className={`text-sm mt-0.5 tabular-nums inline-flex items-center justify-center w-6 h-6 rounded-full ${isToday ? "ring-1 ring-[#0176D3] font-bold text-[#0176D3]" : hits.length > 0 ? "font-semibold text-amber-900" : "text-zinc-700"}`}>
                              {Number(day.split("-")[2])}
                            </div>
                            {hits.length > 1 && (
                              <div className="text-[9px] font-mono text-amber-700 mt-0.5">{"\u00d7"}{hits.length}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {w.items.length === 0 ? (
                      <div className="px-4 py-3 text-xs text-zinc-500">Nobody double-booked on a shared project.</div>
                    ) : (
                      <div className="divide-y divide-border">
                        {w.items.map((c, i) => {
                          const sev = SEVERITY_META[c.severity] || SEVERITY_META.low;
                          return (
                            <Link
                              key={`${w.key}-${i}`}
                              to={`/projects/${c.project_id}`}
                              data-testid={`conflict-row-${w.key}-${i}`}
                              className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 hover:bg-zinc-50 transition-colors"
                            >
                              {/* WHO first, then where.
                                  The question this tab answers is "who is
                                  double-booked?", so the two names lead as the
                                  row's heading and the project follows as
                                  context. They were the other way round, which
                                  buried the people in small grey type while a
                                  repeated project title took the emphasis — and
                                  three rows of the same project read as one
                                  entry at a glance. The middle column absorbs
                                  the slack the row used to leave empty. */}
                              <div className="min-w-0 basis-56 shrink-0">
                                <div className="font-medium text-sm break-words">
                                  {c.member_a.name} &amp; {c.member_b.name}
                                </div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs text-zinc-600 break-words" title={c.project_title}>
                                  {c.project_title}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <div className="flex items-center gap-1.5">
                                  {[c.member_a, c.member_b].map((m, mi) => (
                                    <span
                                      key={mi}
                                      title={`${m.name} \u2014 ${TYPE_META[m.leave_type]?.label || m.leave_type} (${STATUS_META[m.leave_status]?.label})`}
                                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm border ${TYPE_META[m.leave_type]?.cls || TYPE_META.other.cls}`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_META[m.leave_status]?.dot }} />
                                      {TYPE_META[m.leave_type]?.label || m.leave_type}
                                    </span>
                                  ))}
                                </div>
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-sm border ${sev.bg} ${sev.text} ${sev.border}`}>
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sev.dot }} />
                                  {sev.label}
                                </span>
                                <span className="trk-uppercase-label whitespace-nowrap">
                                  {fmtShort(c.overlap_start)}{"\u2013"}{fmtShort(c.overlap_end)} {"\u00b7"} {c.overlap_days}d
                                </span>
                                <ArrowRightIcon size={14} className="text-zinc-400" />
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </section>
                ))}
              </div>

              {/* Beyond the horizon — still visible, just not in the strip. */}
              {laterWeeks.length > 0 && (
                <div className="pt-2">
                  <div className="flex items-baseline justify-between gap-2 mb-3 pb-2 border-b border-border">
                    <h3 className="font-display text-lg font-bold tracking-tight">Later</h3>
                    <span className="trk-uppercase-label">
                      after {fmtShort(horizonEnd)} {"\u00b7"} {laterCount} conflict{laterCount > 1 ? "s" : ""}
                    </span>
                  </div>
                  <PagedList items={laterWeeks} pageSize={4} testIdPrefix="later-conflicts">
                    {(weeks) => (
                      <div className="space-y-3">
                        {weeks.map((w) => (
                          <div key={w.key} className="border border-border rounded-sm" data-testid={`later-conflict-week-${w.key}`}>
                            <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2 bg-zinc-50/60 border-b border-border">
                              <span className="font-medium text-sm">{w.label}</span>
                              <span className="trk-uppercase-label">
                                {w.range} {"\u00b7"} {w.items.length} conflict{w.items.length > 1 ? "s" : ""}
                              </span>
                            </div>
                            <div className="divide-y divide-border">
                              {w.items.map((c, i) => (
                                <Link
                                  key={`${w.key}-${i}`}
                                  to={`/projects/${c.project_id}`}
                                  data-testid={`later-conflict-row-${w.key}-${i}`}
                                  className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 hover:bg-zinc-50 transition-colors"
                                >
                                  {/* Same order as the week rows above: people, then project. */}
                                  <div className="min-w-0 basis-56 shrink-0">
                                    <div className="font-medium text-sm break-words">{c.member_a.name} &amp; {c.member_b.name}</div>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs text-zinc-600 break-words" title={c.project_title}>{c.project_title}</div>
                                  </div>
                                  <span className="trk-uppercase-label whitespace-nowrap">
                                    {fmtShort(c.overlap_start)}{"\u2013"}{fmtShort(c.overlap_end)} {"\u00b7"} {c.overlap_days}d
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </PagedList>
                </div>
              )}
            </div>
          </TabsContent>

          {/* --- HOLIDAYS --- */}
          <TabsContent value="holidays">
            <HolidaysPanel holidays={holidays} isAdmin={currentUser?.role === "admin"} onChanged={onHolidaysChanged} />
          </TabsContent>
        </Tabs>
      </div>
      {editDialog}
    </AppLayout>
  );
}
